import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orderAPI, locationAPI, gatepassAPI } from '../services/api';
import { FiMapPin, FiPackage, FiKey, FiPlus, FiArrowRight } from 'react-icons/fi';
import './Home.css';

const Home = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ orders: 0, locations: 0, gatePasses: 0 });
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, locationsRes, passesRes] = await Promise.all([
        orderAPI.getAll({ limit: 5 }),
        locationAPI.getAll(),
        gatepassAPI.getActive()
      ]);

      setActiveOrders(ordersRes.data.orders || []);
      setStats({
        orders: ordersRes.data.total || 0,
        locations: locationsRes.data.count || 0,
        gatePasses: passesRes.data.count || 0
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: 'info',
      confirmed: 'info',
      preparing: 'warning',
      picked_up: 'warning',
      out_for_delivery: 'warning',
      near_location: 'success',
      delivered: 'success',
      cancelled: 'danger'
    };
    return colors[status] || 'info';
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      swiggy: '🍔',
      zomato: '🍕',
      bigbasket: '🛒',
      blinkit: '⚡'
    };
    return icons[platform] || '📦';
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="home-page">
      <div className="container">
        <header className="home-header">
          <h1>Welcome{user?.name ? `, ${user.name}` : ''}!</h1>
          <p>Manage your deliveries seamlessly</p>
        </header>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon"><FiPackage /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.orders}</span>
              <span className="stat-label">Total Orders</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FiMapPin /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.locations}</span>
              <span className="stat-label">Saved Locations</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><FiKey /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.gatePasses}</span>
              <span className="stat-label">Active Passes</span>
            </div>
          </div>
        </div>

        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-buttons">
            <Link to="/locations/new" className="action-btn">
              <FiPlus />
              <span>Add Location</span>
            </Link>
            <Link to="/orders/new" className="action-btn">
              <FiPackage />
              <span>Register Order</span>
            </Link>
          </div>
        </div>

        <div className="recent-orders">
          <div className="section-header">
            <h2>Recent Orders</h2>
            <Link to="/orders" className="view-all">
              View All <FiArrowRight />
            </Link>
          </div>

          {activeOrders.length === 0 ? (
            <div className="empty-state">
              <p>No orders yet</p>
              <Link to="/orders/new" className="btn btn-primary">
                Register Your First Order
              </Link>
            </div>
          ) : (
            <div className="orders-list">
              {activeOrders.map(order => (
                <Link to={`/orders/${order._id}`} key={order._id} className="order-card">
                  <div className="order-platform">
                    <span className="platform-icon">{getPlatformIcon(order.platform)}</span>
                    <span className="platform-name">{order.platform}</span>
                  </div>
                  <div className="order-info">
                    <span className="order-id">#{order.platformOrderId}</span>
                    <span className={`badge badge-${getStatusColor(order.status)}`}>
                      {order.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <FiArrowRight className="order-arrow" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
